import { useEffect, useRef, useState } from 'react';
import { EStorageKey } from 'common/const';
import { useStorage } from 'common/hooks/useStorage';
import { joinLegacyName } from 'common/helpers/payerContact.helper';
import { IPayerContactForm, ISavedPayerContact } from 'common/types/payerContact.types';

export type TPayerContactField = keyof IPayerContactForm;

const LEGACY_KEYS = [EStorageKey.Name, EStorageKey.Surname, EStorageKey.City, EStorageKey.Email];

/* Контакты гостя на форме оплаты (payer-form, R-5, R-6).
 *  Источник по каждому полю: API (кука), иначе старые ключи localStorage, иначе пусто.
 *  Старые ключи видны сразу; ответ API подставляется только в поля, которых гость ещё не касался,
 *  отказ или медленный ответ ничего не очищают. На устройстве хук ничего не пишет: контакты сохраняет
 *  кука, которую ставит успешный start (R-3). */
export const usePayerContact = (fetchSavedContact: () => Promise<ISavedPayerContact | null>) => {
  const { getStorageItem, removeStorageItem } = useStorage();

  // Хранилище может быть недоступно (приватный режим, запрет): тогда старых данных нет (R-5)
  const readLegacyContact = (): IPayerContactForm => {
    try {
      return {
        name: joinLegacyName(getStorageItem(EStorageKey.Name), getStorageItem(EStorageKey.Surname)),
        email: getStorageItem(EStorageKey.Email)?.trim() ?? '',
      };
    } catch {
      return { name: '', email: '' };
    }
  };

  // Отказ удаления не мешает оплате: ключи останутся до следующей успешной оплаты (R-5)
  const forgetLegacyContact = () => {
    try {
      LEGACY_KEYS.forEach((key) => removeStorageItem(key));
    } catch {
      // ключи удалятся при следующей успешной оплате
    }
  };

  const [contact, setContact] = useState<IPayerContactForm>(readLegacyContact);
  const editedRef = useRef<Record<TPayerContactField, boolean>>({ name: false, email: false });

  const changeContact = (field: TPayerContactField, value: string) => {
    editedRef.current[field] = true;
    setContact((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    let active = true;

    fetchSavedContact().then((saved) => {
      if (!active || !saved) {
        return;
      }

      const pick = (field: TPayerContactField, current: string): string => {
        const value = saved[field];

        return value !== null && !editedRef.current[field] ? value : current;
      };

      setContact((prev) => ({ name: pick('name', prev.name), email: pick('email', prev.email) }));

      // Оба поля от API: старые ключи больше не источник (R-5)
      if (saved.name !== null && saved.email !== null) {
        forgetLegacyContact();
      }
    });

    // Сохранённые контакты читаются один раз при открытии формы
    return () => {
      active = false;
    };
  }, []);

  return { contact, changeContact, forgetLegacyContact };
};
